import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useTenantUsers } from "@/hooks/useTenantUsers";
import type { Account, AccountWriteKind, BusinessEntity, TenantUser } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, SETUP_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import { DialogShell } from "@/components/DialogShell";
import "./Access.css";
import "./ChartOfAccounts.css";
import "./Entities.css";
import "./PaySchedule.css";
// ef-062-8 anchor: the one seam the starting-check-number setting renders into (a stub until the builder replaces the component).
import { EntityTaxControl } from "@/components/EntityTaxControl";
import { CheckNumberSetting } from "@/components/CheckNumberSetting";

/* No props. It had an `onNavigate` for its "back to payroll" control, which a top-level
   nav destination does not need — the sidebar is the way out. */

/** Mirrors `AccountEndpoints.AllowedKinds` — option VALUES are the lowercase strings the
 *  API's own CHECK constraint and `AccountWriteKind` (api/types.ts) both use; labels are
 *  what a farmer reads. */
const KIND_OPTIONS: { value: AccountWriteKind; label: string }[] = [
  { value: "income", label: "Income" },
  { value: "expense", label: "Expense" },
  { value: "asset", label: "Asset" },
  { value: "liability", label: "Liability" },
  { value: "equity", label: "Equity" },
];

function kindLabel(kind: string): string {
  return KIND_OPTIONS.find((k) => k.value === kind.toLowerCase())?.label ?? kind;
}

/** `Account.kind` (the GET read shape) is the PascalCase enum name ("Expense"); the
 *  write routes and this form both speak the lowercase wire form. See
 *  `AccountWriteResult`'s own remarks (api/types.ts) for why the two differ. */
function toWriteKind(kind: string): AccountWriteKind {
  return kind.toLowerCase() as AccountWriteKind;
}

interface AccountFormState {
  number: string;
  name: string;
  kind: AccountWriteKind;
}

function blankAccountForm(): AccountFormState {
  return { number: "", name: "", kind: "expense" };
}

function formFromAccount(account: Account): AccountFormState {
  return { number: account.number, name: account.name, kind: toWriteKind(account.kind) };
}

// ---- ef-047-2: entities ----

interface EntityFormState {
  name: string;
}

function blankEntityForm(): EntityFormState {
  return { name: "" };
}

function formFromEntity(entity: BusinessEntity): EntityFormState {
  return { name: entity.name };
}

/**
 * Who can sign in to this farm — and the control to stop them.
 *
 * <p>
 * This screen exists because 0008 handed seasonal workers logins and gave nobody any way
 * to take one back. Until it, offboarding a worker meant someone with database credentials
 * running UPDATE by hand. On a roster where four of twelve are H-2A visa holders on a
 * fixed season, that is the normal path, not an edge case.
 * </p>
 *
 * <p>
 * The <b>Settings &amp; admin</b> area, reached from the sidebar. It first shipped as a card
 * inside Payroll, on the reasoning that access is about the same people as the roster and
 * that a fifth nav tile would wrap the phone's bottom bar. Both halves were wrong. Buried
 * under Payroll it read as a payroll feature, and nobody wanting to change who can sign in
 * would open Payroll to look for it; the bar was then measured rather than assumed and
 * holds five tiles in one row down to 320px.
 * </p>
 *
 * <p>
 * The area is named more broadly than its contents on purpose. "Who can sign in" was the
 * only section for a while, and ef-044-4 is the first thing to slot in beside it — the
 * farm's own chart of accounts, reached from Setup's Logins tab rather than a sixth tab
 * (`chart-of-accounts.spec.ts`'s own assertion: five tabs, unchanged).
 * </p>
 */
export function Access() {
  const { users, status, error, pendingId, actionError, setActive } = useTenantUsers();

  // ---- ef-048-4: Setup's "Download backup" action ----
  //
  // data-state lives on the BUTTON ITSELF (ui-breadcrumbs rule 1) — a tester reads it
  // directly rather than inferring failure from the absence of a download, same reasoning
  // TimeClock.tsx's own sign-pad state carries. Three states only: idle before the first
  // attempt (and again after a SUCCESSFUL download — nothing more to say once the file is
  // on disk), loading while the request is in flight, error when it failed.
  const [backupState, setBackupState] = useState<"idle" | "loading" | "error">("idle");
  const [backupError, setBackupError] = useState<string | null>(null);

  async function downloadBackup(): Promise<void> {
    setBackupState("loading");
    setBackupError(null);
    try {
      // The bytes are the server's own JSON body verbatim — same "add a blob and a
      // filename and nothing else" discipline YearEnd.tsx's 1099-NEC CSV download
      // follows, bypassing `request<T>` the same way `getBackupExport` itself does.
      const json = await demoApi.getBackupExport();
      const blob = new Blob([json], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-backup-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setBackupState("idle");
    } catch (err) {
      setBackupError(problemMessage(err, "Could not build the backup file."));
      setBackupState("error");
    }
  }

  // ---- ef-044-4: the chart of accounts ----
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [accountsStatus, setAccountsStatus] = useState<"loading" | "ready" | "error">("loading");
  const [accountsError, setAccountsError] = useState<string | null>(null);

  const [form, setForm] = useState<AccountFormState>(blankAccountForm());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archiving, setArchiving] = useState<Account | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  // ef-049-1 — "Import accounts": a raw-CSV chart-of-accounts import, same section as
  // the add/rename form above. ONE persistent button does double duty: with no file
  // chosen yet, a click opens the native file picker; once `importFile` is set, the SAME
  // click submits it instead. This keeps a single accessible name ("Import accounts")
  // through both steps rather than swapping in a second, differently-labeled control —
  // see `setup-account-import.spec.ts`'s own two `getByRole("button", …)` queries, which
  // both resolve to this one element.
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importState, setImportState] = useState<"idle" | "selected" | "uploading" | "error">("idle");
  const [importError, setImportError] = useState<string | null>(null);

  const numberId = useId();
  const nameId = useId();
  const kindId = useId();

  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const archiveTriggerRef = useRef<HTMLButtonElement | null>(null);

  const loadAccounts = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const resolved = await demoApi.getAllAccountsIncludingArchived(signal);
      if (signal?.aborted) return;
      setAccounts(resolved);
      setAccountsStatus("ready");
      setAccountsError(null);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      setAccounts(null);
      setAccountsStatus("error");
      setAccountsError(problemMessage(err, "Could not load the chart of accounts."));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadAccounts(controller.signal);
    return () => controller.abort();
  }, [loadAccounts]);

  function startEdit(account: Account): void {
    setEditingId(account.id);
    setForm(formFromAccount(account));
    setFormError(null);
    setFieldErrors(null);
  }

  function cancelEdit(): void {
    setEditingId(null);
    setForm(blankAccountForm());
    setFormError(null);
    setFieldErrors(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);

    const trimmedNumber = form.number.trim();
    const trimmedName = form.name.trim();

    /*
     * @ui-check: client-side ONLY, and required-ness only — the server's own
     * field-by-field checks (AccountEndpoints.ValidateFields: length bounds on number
     * and name, kind membership) still run after this and remain the actual contract
     * enforcement. This guard exists for the one case a missing value would otherwise
     * reach the network as an empty string rather than a validation error.
     */
    const missing: Record<string, string[]> = {};
    if (!editingId && trimmedNumber === "") missing.number = ["A number is required."];
    if (trimmedName === "") missing.name = ["A name is required."];
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setFormError(Object.values(missing).flat()[0] ?? null);
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        // RENAME ONLY — number and kind are set once at creation and never move
        // (AccountContracts.cs's own UpdateAccountRequest carries no other field).
        await demoApi.renameAccount(editingId, { name: trimmedName });
      } else {
        await demoApi.createAccount({ number: trimmedNumber, name: trimmedName, kind: form.kind });
      }
      await loadAccounts();
      cancelEdit();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not save this account."));
    } finally {
      setSaving(false);
    }
  }

  // ef-049-1 — "Import accounts". See the state block above for why one button covers
  // both the "pick a file" and "submit it" steps.
  function handleImportFileChosen(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportState("selected");
    setImportError(null);
  }

  async function handleImportClick(): Promise<void> {
    if (importState === "uploading") return;

    if (!importFile) {
      importInputRef.current?.click();
      return;
    }

    setImportState("uploading");
    setImportError(null);
    try {
      const csv = await importFile.text();
      await demoApi.importAccounts(csv);
      await loadAccounts();
      setImportFile(null);
      setImportState("idle");
    } catch (err) {
      // Nothing was written on a 400/409 — AccountImportEndpoints' own whole-file
      // refusal (AccountImportTests.cs). Cleared back to idle rather than left
      // "selected" so a retry re-opens the picker instead of resubmitting the same
      // rejected file silently.
      setImportFile(null);
      setImportState("error");
      setImportError(problemMessage(err, "Could not import accounts."));
    } finally {
      // Clear the input so picking the SAME file twice still fires a change event —
      // same discipline ReceiptScan.tsx's own handleFile follows.
      if (importInputRef.current) importInputRef.current.value = "";
    }
  }

  function openArchiveConfirm(account: Account, trigger: HTMLButtonElement | null): void {
    archiveTriggerRef.current = trigger;
    setArchiving(account);
    setArchiveError(null);
  }

  function closeArchiveConfirm(): void {
    setArchiving(null);
    setArchiveError(null);
    archiveTriggerRef.current?.focus();
  }

  async function confirmArchive(): Promise<void> {
    if (!archiving) return;
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      const archivedId = archiving.id;
      await demoApi.archiveAccount(archivedId);
      await loadAccounts();
      setArchiving(null);
      // Editing the very row being archived would submit a rename against an account
      // that no longer resolves active — leave the shared form in add mode instead.
      if (editingId === archivedId) cancelEdit();
    } catch (err) {
      setArchiveError(problemMessage(err, "Could not archive this account."));
    } finally {
      setArchiveBusy(false);
    }
  }

  async function handleRestore(account: Account): Promise<void> {
    setRestoringId(account.id);
    setRestoreError(null);
    try {
      await demoApi.restoreAccount(account.id);
      await loadAccounts();
    } catch (err) {
      setRestoreError(problemMessage(err, "Could not restore this account."));
    } finally {
      setRestoringId(null);
    }
  }

  const activeAccounts = accounts === null ? null : accounts.filter((a) => a.archivedAt === null);
  const archivedAccounts = accounts === null ? [] : accounts.filter((a) => a.archivedAt !== null);
  const activeCount = activeAccounts?.length ?? 0;

  // ---- ef-047-2: entities ----
  const [entities, setEntities] = useState<BusinessEntity[] | null>(null);
  const [entitiesStatus, setEntitiesStatus] = useState<"loading" | "ready" | "error">("loading");
  const [entitiesError, setEntitiesError] = useState<string | null>(null);

  const [entityForm, setEntityForm] = useState<EntityFormState>(blankEntityForm());
  const [editingEntityId, setEditingEntityId] = useState<string | null>(null);
  const [entitySaving, setEntitySaving] = useState(false);
  const [entityFormError, setEntityFormError] = useState<string | null>(null);
  const [entityFieldErrors, setEntityFieldErrors] = useState<Record<string, string[]> | null>(null);

  const [archivedEntitiesOpen, setArchivedEntitiesOpen] = useState(false);
  const [archivingEntity, setArchivingEntity] = useState<BusinessEntity | null>(null);
  const [entityArchiveBusy, setEntityArchiveBusy] = useState(false);
  const [entityArchiveError, setEntityArchiveError] = useState<string | null>(null);

  const [restoringEntityId, setRestoringEntityId] = useState<string | null>(null);
  const [entityRestoreError, setEntityRestoreError] = useState<string | null>(null);

  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [setDefaultError, setSetDefaultError] = useState<string | null>(null);

  const entityNameId = useId();

  const entityCancelBtnRef = useRef<HTMLButtonElement>(null);
  const entityConfirmBtnRef = useRef<HTMLButtonElement>(null);
  const entityArchiveTriggerRef = useRef<HTMLButtonElement | null>(null);

  const loadEntities = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const resolved = await demoApi.getAllEntitiesIncludingArchived(signal);
      if (signal?.aborted) return;
      setEntities(resolved);
      setEntitiesStatus("ready");
      setEntitiesError(null);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      setEntities(null);
      setEntitiesStatus("error");
      setEntitiesError(problemMessage(err, "Could not load the entities."));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadEntities(controller.signal);
    return () => controller.abort();
  }, [loadEntities]);

  function startEditEntity(entity: BusinessEntity): void {
    setEditingEntityId(entity.id);
    setEntityForm(formFromEntity(entity));
    setEntityFormError(null);
    setEntityFieldErrors(null);
  }

  function cancelEditEntity(): void {
    setEditingEntityId(null);
    setEntityForm(blankEntityForm());
    setEntityFormError(null);
    setEntityFieldErrors(null);
  }

  async function handleEntitySubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setEntityFormError(null);
    setEntityFieldErrors(null);

    const trimmedName = entityForm.name.trim();

    /*
     * @ui-check: client-side ONLY, required-ness only — the server's own field-by-field
     * checks (EntityAdminEndpoints' length bound, name membership) still run after this
     * and remain the actual contract enforcement.
     */
    if (trimmedName === "") {
      setEntityFieldErrors({ name: ["A name is required."] });
      setEntityFormError("A name is required.");
      return;
    }

    setEntitySaving(true);
    try {
      if (editingEntityId) {
        await demoApi.renameEntity(editingEntityId, { name: trimmedName });
      } else {
        await demoApi.createEntity({ name: trimmedName });
      }
      await loadEntities();
      cancelEditEntity();
    } catch (err) {
      setEntityFieldErrors(problemFieldErrors(err));
      setEntityFormError(problemMessage(err, "Could not save this entity."));
    } finally {
      setEntitySaving(false);
    }
  }

  function openEntityArchiveConfirm(entity: BusinessEntity, trigger: HTMLButtonElement | null): void {
    entityArchiveTriggerRef.current = trigger;
    setArchivingEntity(entity);
    setEntityArchiveError(null);
  }

  function closeEntityArchiveConfirm(): void {
    setArchivingEntity(null);
    setEntityArchiveError(null);
    entityArchiveTriggerRef.current?.focus();
  }

  async function confirmEntityArchive(): Promise<void> {
    if (!archivingEntity) return;
    setEntityArchiveBusy(true);
    setEntityArchiveError(null);
    try {
      const archivedId = archivingEntity.id;
      await demoApi.archiveEntity(archivedId);
      await loadEntities();
      setArchivingEntity(null);
      // Editing the very row being archived would submit a rename against an entity
      // that no longer resolves active — leave the shared form in add mode instead.
      if (editingEntityId === archivedId) cancelEditEntity();
    } catch (err) {
      setEntityArchiveError(problemMessage(err, "Could not archive this entity."));
    } finally {
      setEntityArchiveBusy(false);
    }
  }

  async function handleRestoreEntity(entity: BusinessEntity): Promise<void> {
    setRestoringEntityId(entity.id);
    setEntityRestoreError(null);
    try {
      await demoApi.restoreEntity(entity.id);
      await loadEntities();
    } catch (err) {
      setEntityRestoreError(problemMessage(err, "Could not restore this entity."));
    } finally {
      setRestoringEntityId(null);
    }
  }

  async function handleSetDefault(entity: BusinessEntity): Promise<void> {
    setSettingDefaultId(entity.id);
    setSetDefaultError(null);
    try {
      await demoApi.setDefaultEntity(entity.id);
      await loadEntities();
    } catch (err) {
      setSetDefaultError(problemMessage(err, "Could not set this entity as the default."));
    } finally {
      setSettingDefaultId(null);
    }
  }

  const activeEntities = entities === null ? null : entities.filter((e) => e.archivedAt === null);
  const archivedEntities = entities === null ? [] : entities.filter((e) => e.archivedAt !== null);
  const activeEntityCount = activeEntities?.length ?? 0;

  // ---- ef-047-3: pay schedule ----
  // Wires the EXISTING `PATCH /entities/{id}/pay-schedule` (0024, ef-015) to a screen for
  // the first time — reads the same `entities`/`activeEntities`/`loadEntities` the Entities
  // section above already maintains, rather than a second fetch.
  const [payScheduleEntityId, setPayScheduleEntityId] = useState<string>("");
  const [payScheduleForm, setPayScheduleForm] = useState<{ paySchedule: string; firstPayday: string }>({
    paySchedule: "weekly",
    firstPayday: "",
  });
  const [payScheduleSaving, setPayScheduleSaving] = useState(false);
  const [payScheduleSaved, setPayScheduleSaved] = useState(false);
  const [payScheduleFormError, setPayScheduleFormError] = useState<string | null>(null);
  const [payScheduleFieldErrors, setPayScheduleFieldErrors] = useState<Record<string, string[]> | null>(
    null,
  );

  const payScheduleEntityFieldId = useId();
  const payScheduleFirstPaydayId = useId();

  function syncPayScheduleForm(entity: BusinessEntity): void {
    setPayScheduleForm({ paySchedule: entity.paySchedule, firstPayday: entity.firstPayday ?? "" });
  }

  // Defaults the picker to the tenant's own default entity (falling back to the first
  // active one) the moment the entity list is ready — but only ONCE, and never after a
  // real selection has happened.
  //
  // A REF, not the `payScheduleEntityId` STATE, is what guards that "once" — this effect
  // is deferred (passive effects flush after paint), and under load that flush can land
  // AFTER a fast subsequent interaction has already set `payScheduleEntityId`. A guard
  // that reads `payScheduleEntityId` off THIS render's closure is stale by the time it
  // runs: it still sees the "" it captured when it was scheduled, not the id the
  // interaction already committed, so it "defaults" the picker right back over that
  // selection. A ref has no per-render closure — `handlePayScheduleEntityChange` flips it
  // synchronously the moment a selection happens, so whichever runs second (the deferred
  // effect or the interaction) always sees the truth.
  //
  // Reproduced via a captured gate trace, never standalone: `saving weekly + a first
  // payday for Trucking round-trips through a reload` (ef-047-3) selected "Larson
  // Trucking LLC" and read the picker straight back to "Larson Family Farms" / biweekly —
  // the DOM snapshot right after the entity list first rendered (options present, radios
  // still their `weekly`-by-coincidence initial state) proved the default-selection effect
  // was still pending at that point, only landing after the test's own selection. Fast,
  // uncontended runs never open the window between "options rendered" and "effect
  // flushed" wide enough for an external event to land in it.
  const payScheduleDefaultedRef = useRef(false);

  useEffect(() => {
    if (payScheduleDefaultedRef.current || activeEntities === null || activeEntities.length === 0) return;
    const [firstActive] = activeEntities;
    const initial = activeEntities.find((e) => e.isDefault) ?? firstActive;
    if (!initial) return;
    payScheduleDefaultedRef.current = true;
    setPayScheduleEntityId(initial.id);
    syncPayScheduleForm(initial);
  }, [activeEntities]);

  function handlePayScheduleEntityChange(id: string): void {
    payScheduleDefaultedRef.current = true;
    setPayScheduleEntityId(id);
    setPayScheduleSaved(false);
    setPayScheduleFormError(null);
    setPayScheduleFieldErrors(null);
    const entity = entities?.find((e) => e.id === id);
    if (entity) syncPayScheduleForm(entity);
  }

  async function handlePayScheduleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!payScheduleEntityId) return;

    setPayScheduleSaving(true);
    setPayScheduleSaved(false);
    setPayScheduleFormError(null);
    setPayScheduleFieldErrors(null);
    try {
      const updated = await demoApi.updateEntityPaySchedule(payScheduleEntityId, {
        paySchedule: payScheduleForm.paySchedule,
        firstPayday: payScheduleForm.firstPayday === "" ? null : payScheduleForm.firstPayday,
      });
      // The Entities section above reads the same list — keep it in sync too.
      await loadEntities();
      syncPayScheduleForm(updated);
      setPayScheduleSaved(true);
    } catch (err) {
      setPayScheduleFieldErrors(problemFieldErrors(err));
      setPayScheduleFormError(problemMessage(err, "Could not save this pay schedule."));
    } finally {
      setPayScheduleSaving(false);
    }
  }

  return (
    /*
      A <main>, not a <div>. AppShell deliberately renders no main landmark of its own
      (AppShell.tsx:149) — each page owns exactly one — and this page shipped without it,
      so the screen had no main landmark at all and a screen-reader user had no way to
      jump past the sidebar to the content. The shell's own "Skip to content" link points
      at #app-shell-content, which existed; the landmark it is meant to lead to did not.
      Caught by the e2e spec's getByRole("main") finding nothing.
    */
    <main className="access" data-state={status}>
      {/*
        PageHeader, not a hand-rolled <h1> — ADR 0009 §3's one-page-template rule applies
        here too (shell.spec.ts's own header comment calls this file out by name as one
        of the two screens still hand-rolling its own heading). Title and subtitle text
        are unchanged from the header this replaces.

        No "back" control — this is a nav destination reached from the Setup gear, and
        the area tab strip below is the way around Setup's own three tabs.
      */}
      <PageHeader
        title="Setup"
        subtitle="The farm's own setup — today, who can sign in and the chart of accounts."
      />

      <AreaTabs area="/setup" tabs={SETUP_TABS} label="Setup" />

      <section className="access__section" aria-labelledby="access-signin">
        <h2 className="access__section-title" id="access-signin">
          Who can sign in
        </h2>
        <p className="access__lede">
          Every login on this farm. Switching one off ends whatever session that person is
          already holding — they are signed out on their next tap, not at the end of the day.
        </p>

      {/*
        A refusal, not a load failure. The server declines two changes with 409 — your own
        login, and the last active owner — and says which. Shown in its own region with
        role="alert" so it is announced when it appears, because it is a response to
        something the reader just did.
      */}
      {actionError && (
        <p className="access__refusal" role="alert">
          {actionError}
        </p>
      )}

      {status === "loading" && (
        <p className="access__note" data-state="loading">
          Loading the access list…
        </p>
      )}

      {status === "error" && (
        <p className="access__note" data-state="error" role="alert">
          {error}
        </p>
      )}

      {status === "ready" && users && (
        <ul className="access__list" aria-labelledby="access-signin" data-state="ready">
          {users.map((user) => (
            <AccessRow
              key={user.id}
              user={user}
              busy={pendingId === user.id}
              onToggle={() => setActive(user.id, !user.isActive)}
            />
          ))}
        </ul>
      )}

      {/*
        ef-048-4 — the tenant backup, in the same Setup/Logins area. Download-only, owner
        session enforced server-side (403 for an employee login, BackupExportEndpoints.cs).
        data-state lives on the button itself per ui-breadcrumbs rule 1 — see
        `downloadBackup`'s own remarks above for the state shape.
      */}
      <p className="access__lede access__backup-lede">
        A full copy of this farm's own records — every account, vendor, entry, and
        document — as one JSON file, for your own safekeeping.
      </p>
      <button
        type="button"
        className="btn btn--ghost access__backup-btn"
        data-state={backupState}
        disabled={backupState === "loading"}
        onClick={() => void downloadBackup()}
      >
        {backupState === "loading" ? "Building the file…" : "Download backup"}
      </button>
      {backupState === "error" && (
        <p className="access__note" role="alert" data-state="error">
          {backupError} Nothing was downloaded.
        </p>
      )}
      </section>

      <section className="access__section coa" aria-labelledby="coa-heading">
        <h2 className="access__section-title" id="coa-heading">
          Chart of accounts
        </h2>
        <p className="access__lede">
          What the farm's own money moves through — add, rename, or archive an account.
          Archiving keeps its posted history readable; it just stops offering the account
          for new entries.
        </p>

        {/*
          ef-049-1 — "Import accounts". A raw CSV (header `number,name,kind`) rather than
          the one-at-a-time form above, for a farm bringing over a chart it already keeps
          elsewhere. ALL OR NOTHING server-side (AccountImportEndpoints.cs): a duplicate
          number or a formula-leading cell rejects the whole file and nothing is written,
          so there is no partial-import state for this control to represent.

          @ui-check: a hidden native `<input type="file">` drives the OS file picker; the
          visible button is the only control a screen reader or Playwright's `getByRole`
          sees. `data-state` on the button carries idle/selected/uploading/error per
          ui-breadcrumbs rule 1 — the label alone ("Import accounts") never changes name
          across the two-step flow, only its behavior does (see the state block above).
        */}
        <div className="coa__import">
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,text/csv"
            className="visually-hidden"
            onChange={handleImportFileChosen}
            aria-hidden="true"
            tabIndex={-1}
          />
          <button
            type="button"
            className="btn btn--ghost"
            data-state={importState}
            aria-busy={importState === "uploading"}
            disabled={importState === "uploading"}
            onClick={() => void handleImportClick()}
          >
            {importState === "uploading" ? "Importing…" : "Import accounts"}
          </button>
          {importState === "selected" && importFile && (
            <p className="coa__hint" role="status">
              {importFile.name} selected — click Import accounts to upload it.
            </p>
          )}
          {importState === "error" && importError && (
            <p className="coa__field-error" role="alert">
              {importError}
            </p>
          )}
        </div>

        <div className="coa__grid">
          <section className="coa__form-card" aria-labelledby="coa-form-heading">
            <h3 id="coa-form-heading" className="coa__card-title">
              {editingId ? "Rename account" : "Add an account"}
            </h3>

            <form className="coa__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
              <div className="coa__field">
                <label htmlFor={numberId}>Number</label>
                <input
                  id={numberId}
                  type="text"
                  value={form.number}
                  maxLength={20}
                  disabled={editingId !== null}
                  onChange={(e) => setForm({ ...form, number: e.target.value })}
                  aria-invalid={fieldErrors?.number ? true : undefined}
                />
                {fieldErrors?.number && (
                  <p className="coa__field-error" role="alert">
                    {fieldErrors.number[0]}
                  </p>
                )}
              </div>

              <div className="coa__field">
                <label htmlFor={nameId}>Name</label>
                <input
                  id={nameId}
                  type="text"
                  value={form.name}
                  maxLength={120}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  aria-invalid={fieldErrors?.name ? true : undefined}
                />
                {fieldErrors?.name && (
                  <p className="coa__field-error" role="alert">
                    {fieldErrors.name[0]}
                  </p>
                )}
              </div>

              <div className="coa__field">
                <label htmlFor={kindId}>Kind</label>
                <select
                  id={kindId}
                  value={form.kind}
                  disabled={editingId !== null}
                  onChange={(e) => setForm({ ...form, kind: e.target.value as AccountWriteKind })}
                >
                  {KIND_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {editingId && (
                <p className="coa__hint">
                  Number and kind cannot change once an account is created — every entry
                  already posted against it relies on both staying put.
                </p>
              )}

              {formError && !fieldErrors && (
                <p className="coa__form-error" role="alert">
                  {formError}
                </p>
              )}

              <div className="coa__form-actions">
                <button type="submit" className="btn btn--primary" disabled={saving}>
                  {saving ? "Saving…" : editingId ? "Save changes" : "Save account"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </section>

          <section
            className="coa__list-card"
            aria-labelledby="coa-list-heading"
            data-state={accountsStatus === "ready" && activeCount === 0 ? "empty" : accountsStatus}
          >
            <h3 id="coa-list-heading" className="coa__card-title">
              Your accounts
            </h3>

            {accountsStatus === "loading" && (
              <StateMessage tone="loading" title="Loading the chart of accounts…" />
            )}

            {accountsStatus === "error" && (
              <StateMessage
                tone="error"
                title="Could not load the chart of accounts."
                detail={accountsError ?? undefined}
              />
            )}

            {accountsStatus === "ready" && activeCount === 0 && (
              <StateMessage
                tone="empty"
                title="No accounts yet."
                detail="Add the income, expense, asset, liability and equity accounts the farm's own books run through."
              />
            )}

            {accountsStatus === "ready" && activeAccounts !== null && activeCount > 0 && (
              <ul className="coa__list" aria-labelledby="coa-list-heading">
                {activeAccounts.map((account) => (
                  <li key={account.id} className="coa__row">
                    <span className="coa__meta">
                      <span className="coa__number">{account.number}</span>
                      <span className="coa__name">{account.name}</span>
                      <span className="coa__kind">{kindLabel(account.kind)}</span>
                      <span className="coa__schedule-f">
                        {account.scheduleFLine
                          ? `Schedule F ${account.scheduleFLine}`
                          : "Unmapped"}
                      </span>
                    </span>
                    <span className="coa__row-actions">
                      <button type="button" className="btn btn--ghost" onClick={() => startEdit(account)}>
                        Rename
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={(e) => openArchiveConfirm(account, e.currentTarget)}
                      >
                        Archive
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <button
              type="button"
              className="btn btn--ghost coa__archived-toggle"
              aria-expanded={archivedOpen}
              onClick={() => setArchivedOpen((v) => !v)}
            >
              {archivedOpen ? "Hide" : "Show"} archived accounts
              {archivedAccounts.length > 0 ? ` (${archivedAccounts.length})` : ""}
            </button>

            {restoreError && (
              <p className="coa__field-error" role="alert">
                {restoreError}
              </p>
            )}

            {/* A <fieldset>/<legend> pair, not a styled <div> — same reasoning as
                JobCodes.tsx's identical disclosure: the legend gives the group a real
                accessible NAME without a second visible heading repeating the toggle
                button's own label. */}
            {archivedOpen && (
              <fieldset className="coa__archived">
                <legend className="visually-hidden">Archived accounts</legend>
                {archivedAccounts.length === 0 ? (
                  <p className="coa__archived-empty">Nothing archived yet.</p>
                ) : (
                  <ul className="coa__list coa__list--archived">
                    {archivedAccounts.map((account) => (
                      <li key={account.id} className="coa__row coa__row--archived">
                        <span className="coa__meta">
                          <span className="coa__number">{account.number}</span>
                          <span className="coa__name">{account.name}</span>
                          <span className="coa__kind">{kindLabel(account.kind)}</span>
                        </span>
                        <span className="coa__row-actions">
                          <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={() => void handleRestore(account)}
                            disabled={restoringId === account.id}
                            aria-busy={restoringId === account.id}
                          >
                            {restoringId === account.id ? "Restoring…" : "Restore"}
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </fieldset>
            )}
          </section>
        </div>

        {archiving && (
          <DialogShell
            title={<>Archive account {archiving.number} — {archiving.name}?</>}
            titleId="archive-account-title"
            role="alertdialog"
            size="sm"
            onClose={closeArchiveConfirm}
            busy={archiveBusy}
            initialFocus={cancelBtnRef}
            describedBy="archive-account-desc"
            footer={
              <>
                <button
                  ref={cancelBtnRef}
                  type="button"
                  className="btn btn--ghost"
                  onClick={closeArchiveConfirm}
                  disabled={archiveBusy}
                >
                  Cancel
                </button>
                <button
                  ref={confirmBtnRef}
                  type="button"
                  className="btn btn--danger"
                  onClick={() => void confirmArchive()}
                  disabled={archiveBusy}
                >
                  {archiveBusy ? "Archiving…" : "Archive"}
                </button>
              </>
            }
          >
            <p id="archive-account-desc" className="coa__dialog-desc">
              Any entries already posted against this account keep it — archiving never
              touches the ledger. It moves to your archived list, stops appearing when
              recording new entries, and can be restored at any time.
            </p>
            {archiveError && (
              <p className="coa__dialog-error" role="alert">
                {archiveError}
              </p>
            )}
          </DialogShell>
        )}
      </section>

      <section className="access__section ent" aria-labelledby="ent-heading">
        <h2 className="access__section-title" id="ent-heading">
          Entities
        </h2>
        <p className="access__lede">
          The farm's own legal entities — add, rename, archive, or pick the one a fresh
          login lands on by default. Archiving keeps its history readable; it just stops
          offering the entity for new work.
        </p>

        <div className="ent__grid">
          <section className="ent__form-card" aria-labelledby="ent-form-heading">
            <h3 id="ent-form-heading" className="ent__card-title">
              {editingEntityId ? "Rename entity" : "Add an entity"}
            </h3>

            <form className="ent__form" onSubmit={(e) => void handleEntitySubmit(e)} noValidate>
              <div className="ent__field">
                {/* "Entity name", not bare "Name" — the Chart of accounts form above
                    (`chart-of-accounts.spec.ts`'s own pre-existing contract) already owns
                    the unscoped `getByLabel(/^name$/i)` on this same Logins tab; a second
                    bare "Name" here made every unscoped query ambiguous
                    (`rules/ui-breadcrumbs.md` §2 — disambiguate by accessible name, not a
                    test-id, when two role+name pairs would otherwise collide). */}
                <label htmlFor={entityNameId}>Entity name</label>
                <input
                  id={entityNameId}
                  type="text"
                  value={entityForm.name}
                  maxLength={120}
                  onChange={(e) => setEntityForm({ name: e.target.value })}
                  aria-invalid={entityFieldErrors?.name ? true : undefined}
                />
                {entityFieldErrors?.name && (
                  <p className="ent__field-error" role="alert">
                    {entityFieldErrors.name[0]}
                  </p>
                )}
              </div>

              {entityFormError && !entityFieldErrors && (
                <p className="ent__form-error" role="alert">
                  {entityFormError}
                </p>
              )}

              <div className="ent__form-actions">
                <button type="submit" className="btn btn--primary" disabled={entitySaving}>
                  {entitySaving ? "Saving…" : editingEntityId ? "Save changes" : "Save entity"}
                </button>
                {editingEntityId && (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={cancelEditEntity}
                    disabled={entitySaving}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </section>

          <section
            className="ent__list-card"
            aria-labelledby="ent-list-heading"
            data-state={entitiesStatus === "ready" && activeEntityCount === 0 ? "empty" : entitiesStatus}
          >
            <h3 id="ent-list-heading" className="ent__card-title">
              Your entities
            </h3>

            {entitiesStatus === "loading" && (
              <StateMessage tone="loading" title="Loading your entities…" />
            )}

            {entitiesStatus === "error" && (
              <StateMessage
                tone="error"
                title="Could not load the entities."
                detail={entitiesError ?? undefined}
              />
            )}

            {entitiesStatus === "ready" && activeEntityCount === 0 && (
              <StateMessage
                tone="empty"
                title="No entities yet."
                detail="Add the farm's own legal entities — LLC, S-corp, partnership, sole proprietorship."
              />
            )}

            {setDefaultError && (
              <p className="ent__field-error" role="alert">
                {setDefaultError}
              </p>
            )}

            {entitiesStatus === "ready" && activeEntities !== null && activeEntityCount > 0 && (
              <ul className="ent__list" aria-labelledby="ent-list-heading">
                {activeEntities.map((entity) => (
                  <li key={entity.id} className="ent__row" data-state={entity.isDefault ? "default" : "active"}>
                    <span className="ent__meta">
                      <span className="ent__name">{entity.name}</span>
                      {entity.isDefault && <span className="ent__default-badge">Default</span>}
                    </span>
                    <span className="ent__row-actions">
                      <button type="button" className="btn btn--ghost" onClick={() => startEditEntity(entity)}>
                        Rename
                      </button>
                      {!entity.isDefault && (
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => void handleSetDefault(entity)}
                          disabled={settingDefaultId === entity.id}
                          aria-busy={settingDefaultId === entity.id}
                        >
                          {settingDefaultId === entity.id ? "Setting…" : "Set default"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={(e) => openEntityArchiveConfirm(entity, e.currentTarget)}
                      >
                        Archive
                      </button>
                    </span>
                    <EntityTaxControl entityId={entity.id} entityName={entity.name} />
                  </li>
                ))}
              </ul>
            )}

            <button
              type="button"
              className="btn btn--ghost ent__archived-toggle"
              aria-expanded={archivedEntitiesOpen}
              onClick={() => setArchivedEntitiesOpen((v) => !v)}
            >
              {archivedEntitiesOpen ? "Hide" : "Show"} archived entities
              {archivedEntities.length > 0 ? ` (${archivedEntities.length})` : ""}
            </button>

            {entityRestoreError && (
              <p className="ent__field-error" role="alert">
                {entityRestoreError}
              </p>
            )}

            {/* A <fieldset>/<legend> pair, not a styled <div> — same reasoning as the
                chart of accounts' identical disclosure above: the legend gives the group
                a real accessible NAME without a second visible heading repeating the
                toggle button's own label. */}
            {archivedEntitiesOpen && (
              <fieldset className="ent__archived">
                <legend className="visually-hidden">Archived entities</legend>
                {archivedEntities.length === 0 ? (
                  <p className="ent__archived-empty">Nothing archived yet.</p>
                ) : (
                  <ul className="ent__list ent__list--archived">
                    {archivedEntities.map((entity) => (
                      <li key={entity.id} className="ent__row ent__row--archived" data-state="archived">
                        <span className="ent__meta">
                          <span className="ent__name">{entity.name}</span>
                        </span>
                        <span className="ent__row-actions">
                          <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={() => void handleRestoreEntity(entity)}
                            disabled={restoringEntityId === entity.id}
                            aria-busy={restoringEntityId === entity.id}
                          >
                            {restoringEntityId === entity.id ? "Restoring…" : "Restore"}
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </fieldset>
            )}
          </section>
        </div>

        {archivingEntity && (
          <DialogShell
            title={<>Archive {archivingEntity.name}?</>}
            titleId="archive-entity-title"
            role="alertdialog"
            size="sm"
            onClose={closeEntityArchiveConfirm}
            busy={entityArchiveBusy}
            initialFocus={entityCancelBtnRef}
            describedBy="archive-entity-desc"
            footer={
              <>
                <button
                  ref={entityCancelBtnRef}
                  type="button"
                  className="btn btn--ghost"
                  onClick={closeEntityArchiveConfirm}
                  disabled={entityArchiveBusy}
                >
                  Cancel
                </button>
                <button
                  ref={entityConfirmBtnRef}
                  type="button"
                  className="btn btn--danger"
                  onClick={() => void confirmEntityArchive()}
                  disabled={entityArchiveBusy}
                >
                  {entityArchiveBusy ? "Archiving…" : "Archive"}
                </button>
              </>
            }
          >
            <p id="archive-entity-desc" className="ent__dialog-desc">
              Any history already recorded under this entity keeps it — archiving never
              touches it. It moves to your archived list, stops appearing when
              recording new work, and can be restored at any time. If this is your
              default entity, archiving clears the default — nothing else becomes
              default automatically.
            </p>
            {entityArchiveError && (
              <p className="ent__dialog-error" role="alert">
                {entityArchiveError}
              </p>
            )}
          </DialogShell>
        )}
      </section>

      <section className="access__section payschedule" aria-labelledby="payschedule-heading">
        <h2 className="access__section-title" id="payschedule-heading">
          Pay schedule
        </h2>
        <p className="access__lede">
          How often each entity pays, and the anchor payday every future period is measured
          from. Changing it moves every period boundary this entity's workers sign
          against and its owner reports against from here on — never anything already
          recorded.
        </p>

        <form
          className="payschedule__form"
          onSubmit={(e) => void handlePayScheduleSubmit(e)}
          noValidate
        >
          <div className="payschedule__field">
            <label htmlFor={payScheduleEntityFieldId}>Entity</label>
            <select
              id={payScheduleEntityFieldId}
              value={payScheduleEntityId}
              onChange={(e) => handlePayScheduleEntityChange(e.target.value)}
            >
              {/* The `label` ATTRIBUTE, not child text — the browser renders and Playwright's
                  own `selectOption({ label })` matches THAT (verified against a throwaway
                  page), while an unscoped `page.getByText(name)` elsewhere on /setup reads
                  child text content and never this option — same name, on purpose, without
                  colliding with the Entities section's own `.ent__name` row for it
                  (`access-entities.spec.ts`'s page-wide visibility check). */}
              {(activeEntities ?? []).map((entity) => (
                <option key={entity.id} value={entity.id} label={entity.name} />
              ))}
            </select>
          </div>

          <fieldset className="payschedule__cadence">
            <legend>Pay period length</legend>
            <label className="payschedule__radio">
              <input
                type="radio"
                name="pay-schedule-cadence"
                value="weekly"
                checked={payScheduleForm.paySchedule === "weekly"}
                onChange={() => setPayScheduleForm((f) => ({ ...f, paySchedule: "weekly" }))}
              />
              <span>Weekly</span>
            </label>
            <label className="payschedule__radio">
              <input
                type="radio"
                name="pay-schedule-cadence"
                value="biweekly"
                checked={payScheduleForm.paySchedule === "biweekly"}
                onChange={() => setPayScheduleForm((f) => ({ ...f, paySchedule: "biweekly" }))}
              />
              <span>Every 2 weeks</span>
            </label>
            {payScheduleFieldErrors?.paySchedule && (
              <p className="payschedule__field-error" role="alert">
                {payScheduleFieldErrors.paySchedule[0]}
              </p>
            )}
          </fieldset>

          <div className="payschedule__field">
            <label htmlFor={payScheduleFirstPaydayId}>First payday</label>
            <input
              id={payScheduleFirstPaydayId}
              type="date"
              value={payScheduleForm.firstPayday}
              onChange={(e) =>
                setPayScheduleForm((f) => ({ ...f, firstPayday: e.target.value }))
              }
              aria-invalid={payScheduleFieldErrors?.firstPayday ? true : undefined}
            />
            {payScheduleFieldErrors?.firstPayday && (
              <p className="payschedule__field-error" role="alert">
                {payScheduleFieldErrors.firstPayday[0]}
              </p>
            )}
          </div>

          {payScheduleFormError && !payScheduleFieldErrors && (
            <p className="payschedule__form-error" role="alert">
              {payScheduleFormError}
            </p>
          )}

          <div className="payschedule__form-actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={payScheduleSaving || !payScheduleEntityId}
            >
              {payScheduleSaving ? "Saving…" : "Save pay schedule"}
            </button>
            {payScheduleSaved && !payScheduleSaving && (
              <span className="payschedule__saved" role="status">
                Saved.
              </span>
            )}
          </div>
        </form>
      </section>

      {/* ef-062-8 anchor - "Check numbering" (the builder REPLACES the component; this line stays). */}
      <CheckNumberSetting />
    </main>
  );
}

function AccessRow({
  user,
  busy,
  onToggle,
}: {
  user: TenantUser;
  busy: boolean;
  onToggle: () => void;
}) {
  // The person, when this login speaks for one. An owner login has no employee behind it,
  // so the address is the only name there is.
  const name = user.employeeName ?? user.email;
  const owner = user.role === "owner";

  return (
    <li className="access__row" data-state={user.isActive ? "active" : "revoked"}>
      <div className="access__who">
        <p className="access__name">
          {name}
          {owner && <span className="access__role">Owner</span>}
          {/* The reader's own row. Named rather than left for them to work out from the
              address, since it is the one row with no control beside it. */}
          {user.isSelf && <span className="access__role access__role--self">You</span>}
        </p>
        {user.employeeName && <p className="access__email">{user.email}</p>}
        {!user.isActive && user.revokedAt && (
          <p className="access__revoked">
            Switched off {new Date(user.revokedAt).toLocaleDateString()}
          </p>
        )}
      </div>

      {/*
        No control on the reader's own row. The server refuses it regardless
        (AuthRepository.SetRevocationAsync), so this is presentation, not enforcement —
        it just declines to offer a click whose only outcome is a refusal.
      */}
      {!user.isSelf && (
        <button
          type="button"
          className={`btn ${user.isActive ? "btn--ghost" : "btn--primary"} access__toggle`}
          onClick={onToggle}
          disabled={busy}
          aria-busy={busy}
        >
          {busy ? "Working…" : user.isActive ? "Switch off" : "Restore"}
        </button>
      )}
    </li>
  );
}
